import { DeviceEntryProps, DeviceType, TCoords } from '@ixicare/resources';
import { NavigateFunction } from 'react-router-dom';
import { FolderInput, Map, Settings, SquarePen, Trash2 } from 'lucide-react';
import { TableRowAction } from './hooks/useTableRowActions';
import { t } from '~i18n';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';

export type ConfigurableDevice = DeviceEntryProps & { type: DeviceType };

const isConfigurable = (dbg: DBGWithType): dbg is ConfigurableDevice => 'uuid' in dbg && typeof dbg.uuid === 'string';

type MapLocation = {
  planeId?: number;
  installationCoords?: TCoords;
  id: number | string;
};

export const navigateMapAction = <T extends MapLocation>(
  navigate: NavigateFunction,
  siteUuid: string | undefined,
  isArchived: boolean,
  markerType: string | ((item: T) => string)
): TableRowAction<T> => ({
  icon: 'residence-map',
  lucide: Map,
  label: t('devices.table.map'),
  // TODO(bulk): acts on the first selected row until bulk behavior is wired.
  onClick: (items) => {
    const item = items[0];
    if (!item) return;
    const type = typeof markerType === 'function' ? markerType(item) : markerType;
    navigate(`/sites/${siteUuid}/${item.planeId}?marker=${type.toLowerCase()}::${item.id}`);
  },
  isVisible: (item) => !!item.installationCoords && !isArchived,
});

export const configAction = (onClick: (items: ConfigurableDevice[]) => void): TableRowAction<DBGWithType> => ({
  icon: 'settings',
  lucide: Settings,
  label: t('devices.table.configuration'),
  onClick: (items) => {
    const configurable = items.filter(isConfigurable);
    if (configurable.length) onClick(configurable);
  },
  isVisible: isConfigurable,
});

export const editAction = <T>(onEdit: (items: T[]) => void): TableRowAction<T> => ({
  icon: 'edit',
  lucide: SquarePen,
  label: t('devices.table.edit'),
  onClick: onEdit,
});

export const moveAction = <T>(onMove: (items: T[]) => void): TableRowAction<T> => ({
  icon: 'move',
  lucide: FolderInput,
  label: t('devices.table.move'),
  onClick: onMove,
});

export const deleteAction = <T>(onDelete: (items: T[]) => void): TableRowAction<T> => ({
  icon: 'delete',
  lucide: Trash2,
  label: t('devices.table.archive'),
  onClick: (items) => {
    if (items.length) onDelete(items);
  },
});
